// @flow
import * as React from 'react';
import Modal from 'react-modal';

import AdminAutoMessageSftpCredentialsModal from 'admin/components/AdminAutoMessageSftpCredentialsModal';
import Button from 'components/Button.jsx';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';
import {
  ComposableTable,
  composeMultipleCells,
  getButtonCellGenerator,
  getTextCellGenerator,
  getTimestampCellGenerator,
} from 'components/ComposableTable';
import ErrorReporter from 'app/components/ErrorReporter.jsx';
import Heading from 'components/Heading.jsx';
import MediaObject from 'components/MediaObject.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';

type Props = {
  autoMessageSftpCredentials: ?(*[]),
  organizationUUID: string,
  refetch: ?() => void,
  loading: ?boolean,
  onCredentialDeletion: (name: string) => void,
};

type State = {
  sftpCredentialsForModal: ?*,
  sftpCredentialsModalIsOpen: boolean,
};

export default class AdminAutoMessageSftpCredentialsView extends React.Component<Props, State> {
  state = {
    sftpCredentialsForModal: undefined,
    sftpCredentialsModalIsOpen: false,
  };

  showSftpCredentialModal = (credentials?: ?*) => {
    this.setState({ sftpCredentialsForModal: credentials }, () =>
      this.setState({ sftpCredentialsModalIsOpen: true })
    );
  };

  hideSftpCredentialModal = () => {
    this.setState({ sftpCredentialsForModal: undefined, sftpCredentialsModalIsOpen: false });
  };

  formatDataToRow = (sftpCredentials: *) => ({
    ...sftpCredentials,
    // TODO: replace with real field when it is working
    lastGenerated: new Date(),
    username: sftpCredentials.credentials.username,
  });

  render() {
    const { loading, autoMessageSftpCredentials, organizationUUID } = this.props;
    const { sftpCredentialsModalIsOpen, sftpCredentialsForModal } = this.state;
    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    if (!autoMessageSftpCredentials) {
      return (
        <ErrorReporter error="AdminAutoMessageSftpCredentialsView - tried to render without credentials" />
      );
    }

    const columnGenerators = [
      composeMultipleCells([
        getTextCellGenerator('displayName', {
          size: 'medium',
          color: 'brand',
          element: 'div',
          lineHeight: 'xlarge',
        }),
        getTimestampCellGenerator(
          'lastGenerated',
          { size: 'xsmall', color: 'thunder' },
          'Last generated:'
        ),
      ]),
      getButtonCellGenerator({
        fieldForAction: 'username',
        onClickAction: data => this.props.onCredentialDeletion(data),
        buttonLabel: 'Delete',
        buttonProps: {
          level: 'secondary',
          color: 'lava',
        },
      }),
    ];

    return (
      <React.Fragment>
        <Modal isOpen={sftpCredentialsModalIsOpen}>
          <AdminAutoMessageSftpCredentialsModal
            orgUuid={organizationUUID}
            onClose={this.hideSftpCredentialModal}
            credentials={sftpCredentialsForModal}
            onCredentialDeletion={() => {
              if (this.props.refetch) {
                this.props.refetch();
              }
            }}
          />
        </Modal>
        <Row>
          <MediaObject center figure="right">
            <Heading flush>Auto messaging SFTP credentials</Heading>
            <Button onClick={() => this.showSftpCredentialModal()}>Generate new credentials</Button>
          </MediaObject>
        </Row>
        <ComposableTable
          columnGenerators={columnGenerators}
          rows={autoMessageSftpCredentials.map(cred => this.formatDataToRow(cred))}
          keyField="displayName"
          rightAlignedLastColumn
          handleRowClick={credentials => this.showSftpCredentialModal(credentials)}
        />
        {autoMessageSftpCredentials.length === 0 && (
          <Text align="center" element="div" size="small" data-test="sftp-creds-null-state">
            <Row size="small">
              <Text weight="semibold">No credentials have been generated yet</Text>
            </Row>
            <Text color="thunder">
              Once you’ve generated a set of credentials to enter into your FTP client, they’ll
              appear here.
            </Text>
          </Text>
        )}
      </React.Fragment>
    );
  }
}
